import { DRAWING_COLORS, LINE_STYLES } from './constants';
import type { SectionPlane } from '@shared/types';
import type { ViewBox } from '@/utils/svgPathParser';

const C = DRAWING_COLORS;
const JP_FONT = "'Noto Sans JP', sans-serif";

type SectionCutLineProps = {
  plane: SectionPlane;
  viewBox: ViewBox;
  vpWidth: number;
  vpHeight: number;
};

/**
 * Renders a section cutting plane line on a source view.
 * - Chain-dot line along the cut axis
 * - Arrows at both ends pointing in the viewing direction
 * - Section label (e.g., "A", "B") at both ends
 */
export function SectionCutLine({ plane, viewBox, vpWidth, vpHeight }: SectionCutLineProps) {
  const label = plane.label?.replace(/-.*$/, '') || 'A';

  // Determine line orientation based on axis and containing view
  const isHorizontal = plane.axis === 'y' || plane.axis === 'z';

  // Normalize position within viewBox
  const normalizedPos = (plane.position - (isHorizontal ? viewBox.minY : viewBox.minX)) /
    (isHorizontal ? viewBox.height : viewBox.width);

  const clampedPos = Math.max(0.1, Math.min(0.9, normalizedPos));

  const arrowSize = 3;
  const labelOffset = 5;

  if (isHorizontal) {
    const y = clampedPos * vpHeight;
    return (
      <g className="section-cut-line">
        <line
          x1={0} y1={y} x2={vpWidth} y2={y}
          stroke={C.line}
          strokeWidth={LINE_STYLES.centerline.strokeWidth}
          strokeDasharray={LINE_STYLES.centerline.dashArray}
        />
        {/* Left arrow and label */}
        <polygon
          points={`${arrowSize},${y - arrowSize / 2} 0,${y} ${arrowSize},${y + arrowSize / 2}`}
          fill={C.line}
        />
        <text x={-labelOffset} y={y} textAnchor="middle" dominantBaseline="central"
          fontSize={3.5} fontWeight="600" fontFamily={JP_FONT} fill={C.text}>
          {label}
        </text>
        {/* Right arrow and label */}
        <polygon
          points={`${vpWidth - arrowSize},${y - arrowSize / 2} ${vpWidth},${y} ${vpWidth - arrowSize},${y + arrowSize / 2}`}
          fill={C.line}
        />
        <text x={vpWidth + labelOffset} y={y} textAnchor="middle" dominantBaseline="central"
          fontSize={3.5} fontWeight="600" fontFamily={JP_FONT} fill={C.text}>
          {label}
        </text>
      </g>
    );
  }

  // Vertical cut line
  const x = clampedPos * vpWidth;
  return (
    <g className="section-cut-line">
      <line
        x1={x} y1={0} x2={x} y2={vpHeight}
        stroke={C.line}
        strokeWidth={LINE_STYLES.centerline.strokeWidth}
        strokeDasharray={LINE_STYLES.centerline.dashArray}
      />
      <polygon
        points={`${x - arrowSize / 2},${arrowSize} ${x},0 ${x + arrowSize / 2},${arrowSize}`}
        fill={C.line}
      />
      <text x={x} y={-labelOffset} textAnchor="middle" dominantBaseline="central"
        fontSize={3.5} fontWeight="600" fontFamily={JP_FONT} fill={C.text}>
        {label}
      </text>
      <polygon
        points={`${x - arrowSize / 2},${vpHeight - arrowSize} ${x},${vpHeight} ${x + arrowSize / 2},${vpHeight - arrowSize}`}
        fill={C.line}
      />
      <text x={x} y={vpHeight + labelOffset} textAnchor="middle" dominantBaseline="central"
        fontSize={3.5} fontWeight="600" fontFamily={JP_FONT} fill={C.text}>
        {label}
      </text>
    </g>
  );
}
